























































































import Result from '@/components/results/result.vue'
import ResetSearch from '@/components/sidebar/reset-search.vue'

import Utilities from '@/mixins/utilities'

import BaseInterface, {BaseAPI} from '@/models/base'
import {CaptureAPI, CharterLegLocation, CharterRouteLeg} from '@/models/capture'
import {DateTime} from '@/models/date-time'
import ResultInterface from '@/models/result'
import SearchData from '@/models/search-data'

import axios from '@/plugins/axios'
import moment from '@/plugins/moment'

import {resolveEnquiryUrl, submitEnquiry, SubmitResult} from '@/services/submit-enquiry'
import {loadTurnstile} from '@/services/turnstile'

import DistanceCalculator from '@/utils/calculators/distance.calculator'
import TimeCalculator from '@/utils/calculators/time.calculator'

import * as Sentry from '@sentry/vue'
import * as _ from 'lodash'
import {mixins} from 'vue-class-component'
import {Component} from 'vue-property-decorator'
import {mapState} from 'vuex'

@Component({
  components: {
    ResetSearch,
    Result,
  },
  computed: {
    ...mapState('capture', [
      'selected',
    ]),
    ...mapState('search', [
      'searchData',
    ]),
  },
  data() {
    return {
      lead: {
        forename: '',
        surname: '',
        mobile: '',
        email: '',
        charterComment: '',
      },
    }
  },
})
export default class Capture extends mixins(Utilities) {
  selected!: ResultInterface
  searchData!: SearchData
  submitting: boolean = false
  turnstileToken: string = ''
  turnstileWidgetId: string | null = null
  submitError: string = ''
  verificationError: boolean = false

  lead: {
    forename: string,
    surname: string,
    mobile: string,
    email: string,
    charterComment: string,
  }

  get buttonDisabled(): boolean {
    return this.lead.forename.length === 0 || this.lead.surname.length === 0 || this.lead.mobile.length === 0 || this.lead.email.length === 0 || this.turnstileToken.length === 0 || this.submitting
  }

  routeTypeIs(routeType: string): boolean {
    return this.searchData.routeType === routeType
  }

  async mounted() {
    await this.renderTurnstile()
  }

  beforeDestroy() {
    if (this.turnstileWidgetId && window.turnstile) {
      window.turnstile.remove(this.turnstileWidgetId)
    }
  }

  async onSubmit() {
    if (!this.selected.calculated) {
      return
    }

    if (this.submitting) {
      return
    }

    this.submitting = true
    this.submitError = ''
    this.verificationError = false

    const data: CaptureAPI = {
      packageId: this.selected.package.id,
      title: ' ',
      ...this.lead,
      landLine: this.lead.mobile,
      CharterQuote: {
        charterFlyingHours: this.selected.calculated.hours.toFixed(1),
        charterPackageCost: this.selected.calculated.packageCost,
        charterPilotDays: this.selected.calculated.days,
        charterPilotRate: this.selected.calculated.pilotRate,
        charterTotalVat: ((this.selected.cost || 0) * 0.2).toFixed(2),
        charterOverrideCurrency: this.$store.getters['global/currency'].toUpperCase(),
        charterReferralSource: this.$store.state.global.entry_url || this.brandSetting('domain'),
      },
      CharterDetails: {
        charterDate: moment(this.searchData.dates).format('YYYY-MM-DD'),
        return: this.searchData.routeType === 'return',
        numberOfDays: this.selected.calculated.days,
        pvpItemId: this.selected.base.closest?.PvpItemId,
      },
      CharterRouteLegs: this.getCharterRouteLegs(this.searchData.destinations),
    }

    data.CharterQuote.charterFlyingHours = _.sumBy(data.CharterRouteLegs, 'ete')

    this.updateSentryContext('capture-data', data)

    const enquiryUrl = resolveEnquiryUrl(process.env)
    if (!enquiryUrl) {
      // tslint:disable-next-line:no-console
      console.debug('Would have posted data', data)
      this.navigateTo('success')
      return
    }

    const result = await submitEnquiry(axios, enquiryUrl, data, this.turnstileToken)
    this.submitting = false

    if (result.kind === 'ok') {
      this.navigateTo('success')
      return
    }

    if (result.kind === 'invalid') {
      // Paths only, never values: they contain customer details.
      Sentry.captureMessage('Enquiry relay rejected payload: ' + result.issuePaths.join(', '), 'warning')
    }

    this.submitError = this.messageFor(result.kind)

    if (result.kind === 'verification_failed') {
      this.verificationError = true
    }
    this.resetTurnstile()
  }

  private async renderTurnstile() {
    const siteKey = process.env.VUE_APP_TURNSTILE_SITE_KEY
    if (!siteKey) {
      Sentry.captureMessage('VUE_APP_TURNSTILE_SITE_KEY is not set', 'error')
      this.submitError = this.contactMessage('Online enquiries are unavailable right now.')
      return
    }
    try {
      const turnstile = await loadTurnstile()
      this.turnstileWidgetId = turnstile.render(this.$refs.turnstile as HTMLElement, {
        sitekey: siteKey,
        action: 'enquiry',
        appearance: 'interaction-only',
        callback: (token: string) => {
          this.turnstileToken = token
          if (this.verificationError) {
            this.submitError = ''
            this.verificationError = false
          }
        },
        'expired-callback': () => {
          this.turnstileToken = ''
        },
        'error-callback': () => {
          this.turnstileToken = ''
          this.submitError = this.messageFor('verification_failed')
          this.verificationError = true
          if (this.turnstileWidgetId && window.turnstile) {
            window.turnstile.reset(this.turnstileWidgetId)
          }
        },
      })
    } catch (error) {
      Sentry.captureException(error)
      this.submitError = this.contactMessage('We couldn\'t load our spam check.')
    }
  }

  private resetTurnstile() {
    this.turnstileToken = ''
    if (this.turnstileWidgetId && window.turnstile) {
      window.turnstile.reset(this.turnstileWidgetId)
    }
  }

  private contactMessage(lead: string): string {
    const title = this.brandSetting('title', 'us')
    const domain = String(this.brandSetting('domain', '')).replace(/\/$/, '')
    return domain ? `${lead} Please contact ${title} via ${domain}.` : `${lead} Please contact ${title} directly.`
  }

  private messageFor(kind: SubmitResult['kind']): string {
    switch (kind) {
      case 'verification_failed':
        return 'We couldn\'t confirm you\'re not a robot. Please submit again.'
      case 'rate_limited':
        return this.contactMessage('You\'ve sent several enquiries in a short time. Please wait a few minutes and try again.')
      case 'upstream_timeout':
        return this.contactMessage('Your enquiry may have been received, but we didn\'t get confirmation in time. Please don\'t submit it again.')
      case 'invalid':
        return this.contactMessage('Some of your enquiry details couldn\'t be accepted. Please check them and try again.')
      default:
        return this.contactMessage('We couldn\'t send your enquiry just now. Please try again in a moment.')
    }
  }

  private getCharterRouteLegs(legs: BaseInterface[]): CharterRouteLeg[] {
    let retlegs

    // legs set the ete & distance to get to that point

    switch (this.$store.getters['search/routeType']) {
      case 'tour':
        retlegs = this.getRouteLegsTour(legs)
        break
      case 'one-way':
        retlegs = this.getRouteLegsOneWay(legs)
        break
      case 'return':
      default:
        retlegs = this.getRouteLegsReturn(legs)
        break
    }

    retlegs = this.addDepartureTimes(retlegs)

    retlegs = this.addBaseLegs(retlegs, legs)

    return retlegs.map((leg, index) => {
      leg.legNumber = index
      return leg
    })
  }

  private addDepartureTimes(charterLegs: CharterRouteLeg[]): CharterRouteLeg[] {
    if (this.searchData.departure && charterLegs[0]) {
      this.setDeparting(charterLegs[0], this.searchData.departure)
    }

    if (this.searchData.arrival && charterLegs[1]) {
      this.setDeparting(charterLegs[1], this.searchData.arrival)
    }

    return charterLegs
  }

  // Leaves departing unset when no usable time was entered, as Azure accepted before the relay existed.
  private setDeparting(leg: CharterRouteLeg, timing: DateTime) {
    if (typeof timing.time !== 'string' || timing.time === '') {
      return
    }
    if (timing.case === 'depart_at') {
      leg.departing = timing.time
      return
    }
    const departing = moment(timing.time, 'HH:mm').subtract(leg.ete as number, 'hours')
    if (departing.isValid()) {
      leg.departing = departing.format('HH:mm')
    }
  }

  private addBaseLegs(charterLegs: CharterRouteLeg[], enquiryLegs: BaseInterface[]): CharterRouteLeg[] {
    const distCalc = new DistanceCalculator
    const timeCalc = new TimeCalculator
    const base = this.selected.base.closest as BaseAPI
    const ret = []

    // start is not the base, need a start->base leg
    if (enquiryLegs[0].venueId !== base.VenueId) {
      const baseDistance = distCalc.distanceBetween({lng: base.CMPLongitude, lat: base.CMPLatitude}, enquiryLegs[0])
      ret.push({
        legNumber: 0,
        origin: {
          locationName: base.VenueName,
          venueId: base.VenueId,
          postcode: base.Postcode,
          latitude: base.CMPLatitude,
          longitude: base.CMPLongitude,
        },
        destination: {
          locationName: enquiryLegs[0].venueName,
          venueId: enquiryLegs[0].venueId,
          postcode: enquiryLegs[0].postcode,
          charterHelipadId: enquiryLegs[0].helipadId,
          latitude: enquiryLegs[0].lat,
          longitude: enquiryLegs[0].lng,
        },
        distance: baseDistance,
        ete: timeCalc.calculate(baseDistance, this.selected.aircraft.cruiseRate),
        emptyLeg: true,
        description: 'position to start',
      } as CharterRouteLeg)
    }

    // add charter legs
    ret.push(...charterLegs)

    // finally need to get back to the base, if we're not there already
    const lastLeg = charterLegs[charterLegs.length - 1]
    if (lastLeg.destination.venueId !== base.VenueId) {
      const baseDistance = distCalc.distanceBetween({lng: base.CMPLongitude, lat: base.CMPLatitude}, {lng: lastLeg.destination.longitude as number, lat: lastLeg.destination.latitude as number})
      ret.push({
        legNumber: 0,
        origin: lastLeg.destination,
        destination: ret[0].origin,
        distance: baseDistance,
        ete: timeCalc.calculate(baseDistance, this.selected.aircraft.cruiseRate),
        emptyLeg: true,
        description: 'back to base',
      })
    }

    return ret
  }

  private getRouteLegsTour(legs: BaseInterface[]): CharterRouteLeg[] {
    const legLocation = {
      locationName: legs[0].venueName,
      venueId: legs[0].venueId,
      charterHelipadId: legs[0].helipadId,
      postcode: legs[0].postcode,
      latitude: legs[0].lat,
      longitude: legs[0].lng,
    } as CharterLegLocation

    return [{
      legNumber: 1,
      distance: null,
      ete: this.$store.getters['search/duration'] / 60,
      emptyLeg: false,
      description: `tour leg around ${legs[0].venueName}`,
      origin: legLocation,
      destination: legLocation,
    } as CharterRouteLeg]
  }

  private getRouteLegsOneWay(legs: BaseInterface[]): CharterRouteLeg[] {
    return this.getRouteLegs(legs)
  }

  private getRouteLegsReturn(legs: BaseInterface[]): CharterRouteLeg[] {
    return this.getRouteLegs(legs).concat(this.getRouteLegs([...legs].reverse()))
  }

  private getRouteLegs(legs: BaseInterface[]): CharterRouteLeg[] {
    const timeCalc = new TimeCalculator
    const distCalc = new DistanceCalculator
    const ret: any[] = []

    const length = legs.length
    for (let index = 1; index < length; ++index) {
      const leg = legs[index]
      const prevLeg = legs[index - 1]
      const distance = distCalc.distanceBetween({lat: leg.lat, lng: leg.lng}, {lat: legs[index - 1].lat, lng: legs[index - 1].lng})

      ret.push({
        legNumber: 0,
        distance,
        ete: distance > 0 ? timeCalc.calculate(distance, this.selected.aircraft.cruiseRate) : null,
        emptyLeg: false,
        description: `${prevLeg.venueName} to ${leg.venueName}`,
        origin: {
          locationName: prevLeg.venueName,
          venueId: prevLeg.venueId,
          charterHelipadId: prevLeg.helipadId,
          postcode: prevLeg.postcode,
          latitude: prevLeg.lat,
          longitude: prevLeg.lng,
        },
        destination: {
          locationName: leg.venueName,
          venueId: leg.venueId,
          charterHelipadId: leg.helipadId,
          postcode: leg.postcode,
          latitude: leg.lat,
          longitude: leg.lng,
        },
      } as CharterRouteLeg)
    }

    return ret
  }
}
